import React, { useState } from "react";
import { Modal, Button, Row, Col, Image } from "antd";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortablePhotoItem = ({ photo, index }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: photo.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={{
                ...style,
                border: '1px dashed #d9d9d9',
                borderRadius: '8px',
                padding: '8px',
                cursor: isDragging ? 'grabbing' : 'move',
                position: 'relative',
                backgroundColor: '#fafafa',
                boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: isDragging ? 1000 : 1,
            }}
            {...attributes}
            {...listeners}
        >
            <div style={{
                position: 'absolute',
                top: '8px',
                left: '8px',
                background: '#1890ff',
                color: 'white',
                borderRadius: '2px 0px 0px',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '500',
                zIndex: 2
            }}>
                {index + 1}
            </div>
            <Image
                src={photo.inspection_image?.image_url || photo.image_url}
                preview={false}
                style={{ width: '100%', height: 'auto', aspectRatio: '1/1', objectFit: 'cover', borderRadius: '4px' }}
            />
        </div>
    );
};

const RearrangeModal = ({
    visible,
    onCancel,
    onSave,
    saving,
    sectionTitle,
    photos: initialPhotos,
    onMovePhoto
}) => {
    const [photos, setPhotos] = useState(initialPhotos);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Update local state when photos prop changes
    React.useEffect(() => {
        setPhotos(initialPhotos);
    }, [initialPhotos]);

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = photos.findIndex(p => p.id === active.id);
            const newIndex = photos.findIndex(p => p.id === over.id);

            const newPhotos = arrayMove(photos, oldIndex, newIndex);
            setPhotos(newPhotos);

            // Call the parent's move handler for optimistic update
            if (onMovePhoto) {
                onMovePhoto(oldIndex, newIndex);
            }
        }
    };

    const handleSave = () => {
        // Pass the reordered photos to the save handler
        if (onSave) {
            onSave(photos);
        }
    };

    return (
        <Modal
            className="full-width-custom-model"
            title={`Rearrange Photos - ${sectionTitle || ''}`}
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button 
                    className="submitBtn" 
                    key="save" 
                    type="primary" 
                    onClick={handleSave} 
                    loading={saving}
                    disabled={!photos || photos.length === 0}
                >
                    Save Order
                </Button>
            ]}
        >
            {!photos || photos.length === 0 ? (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    color: '#999'
                }}>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>No photos in this section</p>
                    <p style={{ fontSize: '14px' }}>Please add photos to the section first</p>
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={photos.map(p => p.id)} strategy={rectSortingStrategy}>
                        <Row gutter={[16, 16]} style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {photos.map((photo, index) => (
                                <Col xs={12} sm={8} md={6} xl={4} key={photo.id}>
                                    <SortablePhotoItem photo={photo} index={index} />
                                </Col>
                            ))}
                        </Row>
                    </SortableContext>
                </DndContext>
            )}
        </Modal>
    );
};

export default RearrangeModal;

